/**
 * La cave › Alertes (/cave/alertes) — alertes groupées par type, action « Ignorer »
 * et brouillon de commande regroupé par fournisseur (quantité proposée, email, envoi).
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Copy, Send, MoreHorizontal } from 'lucide-react';
import { dashboardApi, alertsApi } from '../../services/api';
import { usePublicMode } from '../../context/PublicModeContext';
import { formatEuro } from '../../utils/format';
import { Tag, Spinner, EmptyState, Button } from '../../components/ui/kit';
import { Hearts, MaskedValue } from '../../components/wine/wine';
import { ALERT_LABELS } from '../../constants';

const TYPE_ORDER = ['rupture', 'stock_faible', 'a_boire', 'facture'];

export default function Alertes() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { isPublic } = usePublicMode();
  const { data: alerts = [], isLoading } = useQuery({ queryKey: ['alerts'], queryFn: dashboardApi.alerts });
  const [selected, setSelected] = useState(null);
  const [menuFor, setMenuFor] = useState(null);

  const { data: draft } = useQuery({
    queryKey: ['order-draft', selected?.reference_id],
    queryFn: () => alertsApi.orderDraft(selected.reference_id),
    enabled: !!selected?.reference_id && ['stock_faible', 'rupture'].includes(selected?.type),
  });
  const [emailText, setEmailText] = useState('');

  if (isLoading) return <Spinner />;
  if (alerts.length === 0) return <EmptyState title="Aucune alerte" description="Tout est sous contrôle." testId="alerts-empty" />;

  const grouped = TYPE_ORDER.map((t) => ({ type: t, items: alerts.filter((a) => a.type === t) })).filter((g) => g.items.length);

  const doIgnore = async (a, duration) => {
    setMenuFor(null);
    await alertsApi.ignore({ type: a.type, cuvee_id: a.cuvee_id, duration });
    toast.success('Alerte ignorée');
    qc.invalidateQueries({ queryKey: ['alerts'] });
  };

  const select = (a) => { setSelected(a); setEmailText(''); };

  const sendOrder = async () => {
    const refIds = draft.lines.map((l) => l.reference_id);
    const subject = encodeURIComponent(`Commande — Maison des Monts Bleus`);
    const body = encodeURIComponent(emailText || draft.email_text);
    window.location.href = `mailto:${draft.email || ''}?subject=${subject}&body=${body}`;
    await alertsApi.order({ reference_ids: refIds, supplier_name: draft.supplier_name });
    toast.success('Commande enregistrée · en attente de livraison');
    qc.invalidateQueries({ queryKey: ['alerts'] });
    setSelected(null);
  };

  return (
    <div className="p-4 md:p-6 grid lg:grid-cols-[1fr_400px] gap-6">
      {/* Liste groupée */}
      <div>
        {grouped.map((g) => (
          <section key={g.type} className="mb-6" data-testid={`alert-group-${g.type}`}>
            <p className="kicker mb-2">{ALERT_LABELS[g.type]} · {g.items.length}</p>
            <div className="vd-card">
              {g.items.map((a, i) => (
                <div key={i} className={`flex items-start gap-3 py-3 px-3 hair-b last:border-b-0 ${selected === a ? 'vd-selected' : ''}`}
                  data-testid={`alerte-${a.type}-${i}`}>
                  <button className="flex-1 flex items-start gap-3 text-left" onClick={() => (a.type === 'facture' ? null : select(a))}>
                    <Tag tone={a.type === 'a_boire' ? 'fill' : 'accent'}>{ALERT_LABELS[a.type]}</Tag>
                    <span className="flex-1 min-w-0">
                      <span className="flex items-center gap-2">
                        <span className="font-title text-lg leading-tight">{a.label}</span>
                        {a.hearts > 0 && <Hearts count={a.hearts} size={12} />}
                      </span>
                      <span className="block text-[12.5px] text-n600">{a.detail}</span>
                    </span>
                  </button>
                  <div className="flex items-center gap-2 shrink-0 relative">
                    <button className="text-gold700 text-[13px]" onClick={() => (a.cuvee_id ? navigate(`/cave/cuvee/${a.cuvee_id}`) : null)}>{a.action}</button>
                    {a.cuvee_id && (
                      <button data-testid={`ignore-${a.type}-${i}`} onClick={() => setMenuFor(menuFor === `${a.type}-${i}` ? null : `${a.type}-${i}`)} className="text-n500 hover:text-ink"><MoreHorizontal className="w-4 h-4" /></button>
                    )}
                    {menuFor === `${a.type}-${i}` && (
                      <div className="absolute right-0 top-6 z-30 bg-paper border border-hair rounded-md shadow-md text-[13px] w-40">
                        <p className="kicker px-3 pt-2">Ignorer</p>
                        {[['1w', '1 semaine'], ['1m', '1 mois'], ['always', 'Toujours']].map(([d, l]) => (
                          <button key={d} onClick={() => doIgnore(a, d)} className="w-full text-left px-3 py-1.5 hover:bg-surface" data-testid={`ignore-${d}`}>{l}</button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>

      {/* Brouillon de commande */}
      <div className="lg:border-l lg:border-hair lg:pl-6">
        {!selected || !draft ? (
          <p className="text-n500 text-[14px] pt-4">Sélectionnez une alerte de stock pour préparer une commande.</p>
        ) : (
          <div data-testid="order-draft">
            <p className="kicker mb-1">Brouillon de commande</p>
            <h3 className="font-title text-2xl">{draft.supplier_name}</h3>
            <p className="text-[13px] text-n600">{draft.email || 'email non renseigné'} · délai {draft.delai}</p>
            <div className="mt-3 hair-t">
              {draft.lines.map((l) => (
                <div key={l.reference_id} className="py-2 hair-b text-[13px]" data-testid={`draft-line-${l.reference_id}`}>
                  <div className="flex justify-between">
                    <span>{l.proposed_qty} × {l.name} {l.year}</span>
                    <MaskedValue masked={isPublic}>{formatEuro(l.line_total)}</MaskedValue>
                  </div>
                  <p className="text-[11px] text-n500">{l.explanation}</p>
                </div>
              ))}
            </div>
            <div className="flex justify-between mt-2 text-[14px]">
              <span className="text-n600">Montant estimé HT</span>
              <span className="nums" data-testid="draft-total"><MaskedValue masked={isPublic}>{formatEuro(draft.total_ht)}</MaskedValue></span>
            </div>
            <textarea className="vd-input h-auto py-2 mt-3 text-[13px]" rows={5}
              value={emailText || draft.email_text} onChange={(e) => setEmailText(e.target.value)} data-testid="draft-email" />
            <div className="flex gap-2 mt-3">
              <Button onClick={() => { navigator.clipboard?.writeText(emailText || draft.email_text); toast.success('Copié'); }} data-testid="draft-copy"><Copy className="w-4 h-4" /> Copier</Button>
              <Button variant="primary" className="flex-1" onClick={sendOrder} data-testid="draft-send"><Send className="w-4 h-4" /> Envoyer</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
